import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Grid, 
  Typography, 
  Box, 
  Container, 
  Alert,
  CircularProgress
} from '@mui/material';
import FormQuestions from './components/FormQuestions';
import FormQuestionsPreview from './components/FormQuestionsPreview';
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import Sidebar from './components/Sidebar';
import { postApi } from './api/services';
import { useNavigate } from 'react-router-dom';
import i18next from 'i18next';
import MainLoader from './components/MainLoader';

const ALQIQuestionnaire = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasExistingSurvey, setHasExistingSurvey] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [isCheckingSurvey, setIsCheckingSurvey] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  const alqiQuestions = [
    {
      title: "1. Over the last week, how embarrassed or self-conscious have you felt because of your hair loss?",
      type: "radio",
      commonIdentifier: "embarrassed_self_conscious",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 1
    },
    {
      title: "2. Has your hair loss interfered with your social or romantic life?",
      type: "radio",
      commonIdentifier: "social_romantic_interference",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 2
    },
    {
      title: "3. Has your hair loss made it difficult for you to choose clothes, hats, or hairstyles?",
      type: "radio",
      commonIdentifier: "clothing_hairstyle_difficulty",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 3
    },
    {
      title: "4. Have you avoided mirrors, photos, or video calls due to your hair?",
      type: "radio",
      commonIdentifier: "avoided_mirrors_photos",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 4
    },
    {
      title: "5. Has your hair loss affected your work, studies, or professional confidence?",
      type: "radio",
      commonIdentifier: "work_study_confidence",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 5
    },
    {
      title: "6. Have you felt anxious, depressed, or frustrated because of your hair?",
      type: "radio",
      commonIdentifier: "anxious_depressed_frustrated",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 6
    },
    {
      title: "7. Have you avoided going out or attending events because of your hair?",
      type: "radio",
      commonIdentifier: "avoided_events",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 7
    },
    {
      title: "8. Have you spent time or money trying to hide or fix your hair loss?",
      type: "radio",
      commonIdentifier: "time_money_hiding_fixing",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 8
    },
    {
      title: "9. Have you been bothered by comments or questions about your hair?",
      type: "radio",
      commonIdentifier: "bothered_by_comments",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 9
    },
    {
      title: "10. Overall, how much has your hair loss affected your quality of life this week?",
      type: "radio",
      commonIdentifier: "quality_of_life_impact",
      optionMap: {
        option_1: '0 - Not at all',
        option_2: '1 - A little',
        option_3: '2 - A lot',
        option_4: '3 - Very much'
      },
      questionId: 10
    }
  ];

  // Check for existing survey on component mount
  useEffect(() => {
    checkExistingSurvey();
  }, []);

  const checkExistingSurvey = async () => {
    setIsCheckingSurvey(true);
    try {
      const userId = JSON.parse(localStorage.getItem("user_obj")).user_id;
      const params = { user_id: userId };
      
      console.log('Checking existing ALQI survey with params:', params);
      
      const response = await postApi('list_study_participant_surveys', params);
      
      console.log('ALQI survey check response:', response);
      
      if (response && response.surveys && response.surveys.length > 0) {
        // Survey exists, set up preview mode
        setHasExistingSurvey(true);
        populatePreviewQuestions(response.surveys[0]); // Use the first survey
      } else {
        // No survey exists, show editable form
        setHasExistingSurvey(false);
      }
    } catch (error) {
      console.error('Error checking existing ALQI survey:', error);
      // If API fails, assume no survey exists and show editable form
      setHasExistingSurvey(false);
    } finally {
      setIsCheckingSurvey(false);
    }
  };

  const populatePreviewQuestions = (surveyData) => {
    const questionsWithValues = alqiQuestions.map(question => {
      const questionKey = question.commonIdentifier;
      const surveyValue = surveyData[questionKey];
      
      if (surveyValue !== undefined) {
        // Find the option text that matches the numeric value
        const optionText = Object.values(question.optionMap).find(option => {
          const optionValue = parseInt(option.split(' - ')[0]);
          return optionValue === surveyValue;
        });
        
        return {
          ...question,
          value: optionText || surveyValue.toString()
        };
      }
      return question;
    });
    
    setPreviewQuestions(questionsWithValues);
    
    // Also populate formValues for when user switches to edit mode
    const initialFormValues = {};
    questionsWithValues.forEach(question => {
      if (question.value) {
        initialFormValues[question.commonIdentifier] = question.value;
      }
    });
    setFormValues(initialFormValues);
  };

  const handleFormValues = (value, identifier) => {
    setFormValues(prev => ({
      ...prev,
      [identifier]: value
    }));
  };

  const preparePayload = () => {
    const payload = {
      user_id: JSON.parse(localStorage.getItem("user_obj")).user_id
    };

    Object.keys(formValues).forEach(key => {
      const value = formValues[key];
      const score = parseInt(value.split(' - ')[0]);
      payload[key] = score;
    });

    return payload;
  };

  const handleSubmit = async () => {
    const answeredQuestions = Object.keys(formValues).length;
    if (answeredQuestions < alqiQuestions.length) {
      alert(`Please answer all ${alqiQuestions.length} questions before submitting. You have answered ${answeredQuestions}.`);
      return;
    }

    setIsLoading(true);
    
    try {
      const payload = preparePayload();
      
      console.log('Submitting ALQI payload:', payload);
      
      const response = await postApi('submit_study_participant_survey', payload);
      
      console.log('ALQI submission response:', response);
      setIsSubmitted(true);

      // Redirect immediately after showing success message
      setTimeout(() => {
        navigate('/reports');
      }, 1500);
      
    } catch (error) {
      console.error('ALQI submission failed:', error);
      alert('Failed to submit questionnaire. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  // Show loading while checking for existing survey
  if (isCheckingSurvey) {
    return (
      <>
      <MainLoader />
      <div className="feedbackContainer photo-container main-section-container">
        <Grid container spacing={0} className="d-flex justify-content-start align-items-center header">
          <Grid item xs={1}>
            <div className="d-flex justify-content-start align-items-center">
              <span className="backButtonDiv_feedbackform" onClick={() => navigate(-1)}>
                <ArrowBackOutlinedIcon className="arrowBackBtn_feedbackform" />
              </span>
            </div>
          </Grid>
          <Grid item xs={10} className="d-flex justify-content-center align-items-center reportPageHead">
            <Typography variant="h6" className="title ml-0px">
              Alopecia Life Quality Index
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Sidebar />
          </Grid>
        </Grid>
      </div>
      </>
    );
  }

  return (
    <div className="feedbackContainer photo-container main-section-container">
      <Grid container spacing={0} className="d-flex justify-content-start align-items-center header">
        <Grid item xs={1} className="">
          <div className="d-flex justify-content-start align-items-center">
            <span className="backButtonDiv_feedbackform" onClick={() => navigate('/reports')}>
              <ArrowBackOutlinedIcon className="arrowBackBtn_feedbackform" />
            </span>
          </div>
        </Grid>
        <Grid item xs={10} className="d-flex justify-content-center align-items-center reportPageHead">
          <Typography variant="h6" className="title ml-0px">
            Alopecia Life Quality Index
          </Typography>
        </Grid>
        <Grid item xs={1}>
          <Sidebar />
        </Grid>
      </Grid>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="body1" paragraph sx={{ mb: 3 }} className="instructionText">
          <strong>Instructions:</strong> The following questions ask how your hair loss has affected your life over the past week. Please answer each question by selecting the option that best describes your experience.
        </Typography>

        <div className="questions-container">
          {hasExistingSurvey && !isEditing ? (
            // Show preview mode with all options (one selected)
            previewQuestions.map((question) => (
              <Grid item xs={12} key={question.questionId} sx={{ mb: 4 }}>
                <FormQuestionsPreview 
                  element={question}
                  handleFormValues={handleFormValues}
                  formValues={formValues}
                  formQuestions={previewQuestions}
                  showQuesAns={false} // Changed to false to show all options
                />
              </Grid>
            ))
          ) : (
            // Show editable form (either new survey or editing existing)
            alqiQuestions.map((question) => (
              <Grid item xs={12} key={question.questionId} sx={{ mb: 4 }}>
                <FormQuestions 
                  element={{
                    ...question,
                    value: formValues[question.commonIdentifier] // Prefill with existing values
                  }}
                  handleFormValues={handleFormValues}
                  formValues={formValues}
                  formQuestions={alqiQuestions}
                  from={"alqi"}
                />
              </Grid>
            ))
          )}
        </div>

        {/* Show edit button and info when in preview mode */}
        {hasExistingSurvey && !isEditing && (
          <Box textAlign="center" mt={4}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1">
                You have already submitted this. You can update your responses by clicking the Edit button below.
              </Typography>
            </Alert>
            <Box>
              <Button 
                variant="contained" 
                onClick={handleEdit}
                sx={{ minWidth: 120 }}
                className='commonButton'
              >
                {i18next.t("Edit Responses")}
              </Button>
            </Box>
          </Box>
        )}

        {/* Show submit button when editing or new survey */}
        {(isEditing || !hasExistingSurvey) && (
          <Box textAlign="center" mt={4}>
            <Button
              variant="contained" 
              size="large"
              onClick={handleSubmit}
              className='commonButton'
              disabled={isLoading || isSubmitted || Object.keys(formValues).length < alqiQuestions.length}
              sx={{ 
                minWidth: 200, 
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                  Submitting...
                </Box>
              ) : isSubmitted ? (
                'Submitted!'
              ) : (
                'Submit'
              )}
            </Button>
            
            {Object.keys(formValues).length < alqiQuestions.length && !isSubmitted && (
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Please answer all {alqiQuestions.length} questions to submit ({Object.keys(formValues).length}/{alqiQuestions.length} answered)
              </Typography>
            )}
          </Box>
        )}
      </Container>
    </div>
  );
};

export default ALQIQuestionnaire;